import React, { useState, useCallback } from 'react';
import { Share2, Copy, CheckCircle2, QrCode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import { Practitioner } from '@/types/practitioner';
import { APP_CONFIG } from '@/config/app-config';
import QRCodeStyling from 'qr-code-styling';

interface PractitionerShareButtonProps {
  practitioner: Practitioner;
  className?: string;
}

export const PractitionerShareButton: React.FC<PractitionerShareButtonProps> = ({ 
  practitioner, 
  className = '' 
}) => {
  const { t } = useTranslation('practitioners');
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  
  const profileUrl = `${APP_CONFIG.PRODUCTION_DOMAIN}/book-an-appointment/${practitioner.slug}`;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(profileUrl);
      setCopied(true);
      toast({
        title: t('linkCopied'),
        description: t('linkCopiedDescription'),
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({
        title: t('copyError'),
        description: t('copyErrorDescription'),
        variant: 'destructive',
      });
    }
  };

  const qrCallbackRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      node.innerHTML = '';
      const qrCode = new QRCodeStyling({
        width: 200,
        height: 200,
        data: profileUrl,
        margin: 8,
        dotsOptions: { color: '#92400E', type: 'rounded' },
        cornersSquareOptions: { color: '#78350F', type: 'extra-rounded' },
        cornersDotOptions: { color: '#78350F', type: 'dot' },
        backgroundOptions: { color: '#FFFBEB' },
        imageOptions: { crossOrigin: 'anonymous', margin: 8 }
      });
      qrCode.append(node);
    }
  }, [profileUrl]);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className={className}>
            <Share2 className="h-4 w-4 mr-2" />
            {t('shareButton')}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onClick={copyToClipboard}>
            {copied ? (
              <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
            ) : (
              <Copy className="h-4 w-4 mr-2" />
            )}
            {t('copyLink')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setQrDialogOpen(true)}>
            <QrCode className="h-4 w-4 mr-2" />
            {t('qrCode')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('qrCodeTitle')}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center space-y-4">
            <div className="bg-amber-50 p-4 rounded-2xl border border-amber-200 shadow-sm">
              <div ref={qrCallbackRef} className="rounded-xl overflow-hidden" />
            </div>
            <p className="text-sm text-muted-foreground text-center">
              {t('qrCodeDescription')}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
